import React, { useState } from "react";
import { Lock, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ownerApi, inr, errText } from "@/lib/ownerApi";

const FIELDS = [["extra", "Extra work (+)", "number"], ["deduction", "Deduction (−)", "number"], ["advance", "Advance paid", "number"],
  ["paid_amount", "Amount paid", "number"], ["paid_date", "Payment date", "date"], ["method", "Method (cash/bank/UPI)", "text"], ["note", "Note", "text"]];

const Breakdown = ({ t }) => (
  <>
  <div className="md:hidden space-y-2" data-testid={`owner-salary-breakdown-mobile-${t.key}`}>
    {t.components.map((c, i) => (
      <div key={i} className="rounded-lg bg-stone-50 border border-stone-200 p-2 text-sm">
        <div className="flex justify-between gap-2"><b>{c.label}</b><b>{inr(c.amount_calc)}</b></div>
        <div className="font-mono text-[11px] text-stone-600 break-words">{c.type === "percent" ? `${c.group}: ${c.formula}` : c.formula}</div>
      </div>
    ))}
  </div>
  <div className="overflow-x-auto hidden md:block">
    <table className="min-w-full text-sm" data-testid={`owner-salary-breakdown-${t.key}`}>
      <thead className="text-stone-500 text-xs"><tr><th className="text-left py-1 pr-3">Component</th><th className="text-left pr-3">Group</th><th className="text-left pr-3">Total fees</th><th className="text-left pr-3">%</th><th className="text-left pr-3">Deduction</th><th className="text-left pr-3">Formula</th><th className="text-right">Monthly</th></tr></thead>
      <tbody className="divide-y divide-stone-100">
        {t.components.map((c, i) => (
          <tr key={i} data-testid={`owner-salary-comp-${t.key}-${i}`}>
            <td className="py-1.5 pr-3 font-medium">{c.label}</td>
            <td className="pr-3">{c.type === "percent" ? c.group : "Allowance"}</td>
            <td className="pr-3">{c.type === "percent" ? inr(c.total_fees) : "—"}</td>
            <td className="pr-3">{c.type === "percent" ? `${c.percent}%${c.share !== 100 ? ` × ${c.share}%` : ""}` : "—"}</td>
            <td className="pr-3">{c.type === "percent" && c.deduction ? inr(c.deduction) : "—"}</td>
            <td className="pr-3 font-mono text-xs text-stone-600">{c.formula}</td>
            <td className="text-right font-semibold">{inr(c.amount_calc)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  </>
);

export default function TeacherSalaryCard({ t, month, onDone }) {
  const [v, setV] = useState(Object.fromEntries(FIELDS.map(([k]) => [k, t[k] ?? (k.match(/date|method|note/) ? "" : 0)])));
  const [busy, setBusy] = useState(false);
  const final = t.status === "final";
  const run = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); onDone(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); } };
  const save = () => run(() => ownerApi.put(`/salary/month/${month}/${t.key}`, Object.fromEntries(FIELDS.map(([k, , ty]) => [k, ty === "number" ? parseInt(v[k], 10) || 0 : v[k]]))), "Saved");
  return (
    <div className={`rounded-2xl border bg-white p-4 space-y-3 ${final ? "border-emerald-300" : "border-stone-200"}`} data-testid={`owner-salary-card-${t.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-bold text-stone-900">{t.name} {final && <span className="ml-2 text-xs font-semibold text-emerald-700">Finalised</span>}</div>
        <div className="text-sm">Calculated <b data-testid={`owner-salary-calc-${t.key}`}>{inr(t.calculated)}</b> · Net <b>{inr(t.net_payable)}</b> · Paid <b className="text-emerald-800">{inr(t.total_paid)}</b> · Outstanding <b className="text-amber-800" data-testid={`owner-salary-out-${t.key}`}>{inr(t.outstanding)}</b></div>
      </div>
      <Breakdown t={t} />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {FIELDS.map(([k, label, ty]) => (
          <label key={k} className={`text-[11px] font-semibold text-stone-500 space-y-1 min-w-0 ${ty === "number" ? "" : "col-span-2 md:col-span-1"}`}><span>{label}</span>
            <Input type={ty} disabled={final} value={v[k] ?? ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="w-full min-w-0" data-testid={`owner-salary-${k}-${t.key}`} /></label>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {!final && <Button size="sm" onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" data-testid={`owner-salary-save-${t.key}`}>Save</Button>}
        {!final ? <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => ownerApi.post(`/salary/month/${month}/${t.key}/finalise`), "Month finalised")} data-testid={`owner-salary-finalise-${t.key}`}><Lock className="h-3.5 w-3.5 mr-1" /> Finalise month</Button>
          : <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => ownerApi.post(`/salary/month/${month}/${t.key}/unlock`), "Unlocked")} data-testid={`owner-salary-unlock-${t.key}`}><Unlock className="h-3.5 w-3.5 mr-1" /> Unlock</Button>}
      </div>
    </div>
  );
}
